import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useColors, useI18n } from '@/composables';
import { putUserApiRequest } from '@/services/api/user-api';
import { useStore } from '@/store';

type WarningState = 90 | 100 | 200;

export const useEventsLimitNotice = () => {
  const { t } = useI18n();
  const $colors = useColors();
  const store = useStore();
  const router = useRouter();

  const active = ref(false);
  const tachometerIcon = ref('');
  const tachometerColor = ref('');
  const modalTitle = ref('');
  const text = ref('');
  const warningState = ref<WarningState | null>(null);

  const warningStateCalculate = (): WarningState | null => {
    const { eventsUsed, eventsIncluded } = store.billing;
    const { eventUsageWarningStatus } = store.user;
    const percent = (eventsUsed * 100) / eventsIncluded;

    if (percent >= 200 && ![200].includes(eventUsageWarningStatus)) {
      return 200;
    }

    if (percent < 200 && percent >= 100 && ![100, 200].includes(eventUsageWarningStatus)) {
      return 100;
    }

    if (percent < 100 && percent >= 90 && ![90, 100, 200].includes(eventUsageWarningStatus)) {
      return 90;
    }

    return null;
  };

  const updateText = () => {
    switch (warningState.value) {
      case 90: {
        modalTitle.value = t('Events Limit');
        tachometerIcon.value = 'fa-tachometer-alt-fast';
        text.value = t(
          'You’ve Used 90% of Your Event Limit<br>Once you reach 100%, <b>overage charges</b> will begin to apply. <br>To save on tracking costs, we recommend <button id="goToSubscription">upgrading to a plan</button> with a higher event limit.<br><b>Good news:</b>Your traffic won’t be interrupted when you hit the limit — your campaigns will continue running smoothly.<br>If you need help choosing the right plan, feel free to reach out to us via live chat.',
        );
        tachometerColor.value = $colors.sunglow;
        return;
      }
      case 100: {
        modalTitle.value = t('Events Limit');
        tachometerIcon.value = 'fa-tachometer-alt-fastest';
        text.value = t(
          'You’ve Used {0}% of Events <br>As of now, overage charges are applying and should be paid at the end of billing period.<br>To keep your tracking budget under control, we highly recommend <button id="goToSubscription">upgrading to a plan</button> with more events included. <br>You can continue on the current plan —  <b>your campaigns continue running without any disruption</b>.<br>If you need help choosing the right plan, feel free to reach out to us via live chat.',
          [100],
        );
        tachometerColor.value = $colors.red;
        return;
      }
      case 200: {
        modalTitle.value = t('Events Limit');
        tachometerIcon.value = 'fa-tachometer-alt-fastest';
        text.value = t(
          'You’ve Used {0}% of Events <br>As of now, overage charges are applying and should be paid at the end of billing period.<br>To keep your tracking budget under control, we highly recommend <button id="goToSubscription">upgrading to a plan</button> with more events included. <br>You can continue on the current plan —  <b>your campaigns continue running without any disruption</b>.<br>If you need help choosing the right plan, feel free to reach out to us via live chat.',
          [200],
        );
        tachometerColor.value = $colors.red;
      }
    }
  };

  const bindSubscriptionLink = () => {
    nextTick(() => {
      const subscriptionLink = document.querySelector('#goToSubscription');
      subscriptionLink?.addEventListener('click', goToSubscription);
    });
  };

  const unbindSubscriptionLink = () => {
    document.querySelector('#goToSubscription')?.removeEventListener('click', goToSubscription);
  };

  const warningStateUpdate = () => {
    const { hasSuccessPayment, eventUsageWarningStatus, isMember } = store.user;

    if (hasSuccessPayment === 0 || eventUsageWarningStatus === -1 || isMember) {
      active.value = false;
      return;
    }

    warningState.value = warningStateCalculate();

    if (warningState.value) {
      updateText();
      active.value = true;
      bindSubscriptionLink();
    } else {
      active.value = false;
    }
  };

  const close = () => {
    active.value = false;

    if (!warningState.value) return;

    store.mergeUser({
      eventUsageWarningStatus: warningState.value,
    });

    return putUserApiRequest({
      eventUsageWarningStatus: warningState.value,
    }).then((response) => {
      store.mergeUser(response);
    });
  };

  const goToSubscription = () => {
    close();
    router.push('/app/profile/subscription');
  };

  watch(
    () => store.billing,
    () => {
      warningStateUpdate();
    },
    { deep: true },
  );

  onBeforeUnmount(() => {
    unbindSubscriptionLink();
  });

  return {
    t,
    active,
    tachometerIcon,
    tachometerColor,
    modalTitle,
    text,
    close,
    goToSubscription,
  };
};
